export const normalizePolicyString = (value: unknown): string => {
  return `${value || ''}`.trim().toLowerCase();
};

export const normalizePolicyList = (values: unknown): string[] => {
  if (!Array.isArray(values)) {
    return [];
  }

  const seen = new Set<string>();

  values.forEach((value) => {
    const normalized = normalizePolicyString(value);
    if (!normalized) {
      return;
    }

    seen.add(normalized);
  });

  return Array.from(seen);
};

export const getUserPolicyList = (user: any): string[] => {
  return normalizePolicyList(user?.policies);
};

export const policyMatches = (userPolicy: string, requiredPolicy: string): boolean => {
  if (!userPolicy || !requiredPolicy) {
    return false;
  }

  const normalizedUser = normalizePolicyString(userPolicy);
  const normalizedRequired = normalizePolicyString(requiredPolicy);
  if (!normalizedUser || !normalizedRequired) {
    return false;
  }

  const [userResource, userAction = ''] = normalizedUser.split('~');
  const [requiredResource, requiredAction = ''] = normalizedRequired.split('~');
  if (!userResource || !requiredResource) {
    return false;
  }

  const userParts = userResource.split(':');
  const requiredParts = requiredResource.split(':');

  if (!requiredAction || !userAction) {
    return normalizedUser === normalizedRequired;
  }
  if (userAction !== 'full' && requiredAction !== userAction) {
    return false;
  }

  if (userAction === 'full') {
    if (userParts.length > requiredParts.length) {
      return false;
    }
    return userParts.every((userPart, index) => userPart === requiredParts[index]);
  }

  if (requiredAction === '*' || userAction === '*') {
    return normalizedUser === '*:*~*';
  }
  if (userParts.length !== requiredParts.length) {
    return false;
  }
  return userParts.every((userPart, index) => userPart === requiredParts[index]);
};

const resolvePolicySource = (source: unknown): string[] => {
  if (Array.isArray(source)) {
    return normalizePolicyList(source);
  }

  if (source && typeof source === 'object') {
    return getUserPolicyList(source);
  }

  return [];
};

export const hasPolicy = (source: unknown, requiredPolicy: string): boolean => {
  if (!requiredPolicy) {
    return false;
  }

  const policyList = resolvePolicySource(source);
  if (policyList.includes('*:*~*')) {
    return true;
  }

  return policyList.some((policy) => policyMatches(policy, requiredPolicy));
};

export const hasAnyPolicy = (source: unknown, requiredPolicies: string[]): boolean => {
  if (!Array.isArray(requiredPolicies) || requiredPolicies.length === 0) {
    return false;
  }

  return requiredPolicies.some((requiredPolicy) => hasPolicy(source, requiredPolicy));
};
